export type Currency = {
  name: string;
  /** Lowercase ISO 4217 code, matching the API's `doc_currency` enum casing. */
  code: string;
};

/**
 * Currencies a member can pick when submitting/reviewing a claim receipt. Common travel
 * destinations for New Zealanders, plus the currencies of EAP-only markets whose members submit
 * receipts in their local currency (AED, BDT, BGN, BRL, COP, INR, PHP, PLN, RON, RSD, UYU).
 *
 * Single source of truth — both the marketplace and manage apps re-export this from their own
 * `constants/currency`.
 *
 * https://www.infometrics.co.nz/article/2024-04-chart-of-the-month-the-top-kiwi-travel-destinations
 */
export const CURRENCIES: Currency[] = [
  { name: "New Zealand Dollar", code: "nzd" },
  { name: "Australian Dollar", code: "aud" },
  { name: "United States Dollar", code: "usd" },
  { name: "Canadian Dollar", code: "cad" },
  { name: "Great British Pound", code: "gbp" },
  { name: "Euro", code: "eur" },
  { name: "Singapore Dollar", code: "sgd" },
  { name: "Mexican Peso", code: "mxn" },
  { name: "Hong Kong Dollar", code: "hkd" },
  { name: "Chinese Yuan", code: "cny" },
  { name: "Israeli Shekel", code: "ils" },
  { name: "United Arab Emirates Dirham", code: "aed" },
  { name: "Brazilian Real", code: "brl" },
  { name: "Indian Rupee", code: "inr" },
  { name: "Philippine Peso", code: "php" },
  { name: "Polish Zloty", code: "pln" },
  { name: "Romanian Leu", code: "ron" },
  { name: "Bulgarian Lev", code: "bgn" },
  { name: "Colombian Peso", code: "cop" },
  { name: "Uruguayan Peso", code: "uyu" },
  { name: "Bangladeshi Taka", code: "bdt" },
  { name: "Serbian Dinar", code: "rsd" },
];

CURRENCIES.sort((a, b) => a.name.localeCompare(b.name));

export function dollarsToCents(dollars: number, currency = "NZD") {
  const decimalPlaces = getCurrencyDecimalPlaces(currency);
  return Math.round(dollars * 10 ** decimalPlaces);
}

export function centsToDollars(cents: number, currency = "NZD") {
  const decimalPlaces = getCurrencyDecimalPlaces(currency);
  return cents / 10 ** decimalPlaces;
}

/**
 * Get the size of the "minor unit" for a currency relative to the base unit.
 */
export function getCurrencyDecimalPlaces(currencyCode: string) {
  const upperCaseCurrencyCode = currencyCode.toUpperCase();
  switch (upperCaseCurrencyCode) {
    // Burundi Franc
    case "BIF":
    // Chilean Peso
    case "CLP":
    // Djibouti Franc
    case "DJF":
    // Guinean Franc
    case "GNF":
    // Iceland Krona
    case "ISK":
    // Yen
    case "JPY":
    // Comorian Franc
    case "KMF":
    // Won
    case "KRW":
    // Guarani
    case "PYG":
    // Rwanda Franc
    case "RWF":
    // Uganda Shilling
    case "UGX":
    // Uruguay Peso en Unidades Indexadas (UI)
    case "UYI":
    // Dong
    case "VND":
    // Vatu
    case "VUV":
    // CFA Franc BEAC
    case "XAF":
    // CFA Franc BCEAO
    case "XOF":
    // CFP Franc
    case "XPF":
      return 0;
    // Bahraini Dinar
    case "BHD":
    // Iraqi Dinar
    case "IQD":
    // Jordanian Dinar
    case "JOD":
    // Kuwaiti Dinar
    case "KWD":
    // Libyan Dinar
    case "LYD":
    // Rial Omani
    case "OMR":
    // Tunisian Dinar
    case "TND":
      return 3;
    // Unidad de Fomento
    case "CLF":
    // Unidad Previsional
    case "UYW":
      return 4;
    default:
      return 2;
  }
}
